/**
 * Subscription Context
 *
 * Provides subscription state to all components in the app.
 * Uses a read-through cache pattern with timestamp-based staleness:
 *
 * 1. Initial fetch on authentication
 * 2. Periodic refresh every REFRESH_INTERVAL_MS (5 min) via Mantine useInterval
 * 3. Tab-focus refresh via Mantine useDocumentVisibility -- only if data is stale
 * 4. On-demand refresh via refreshSubscription (after checkout, etc.)
 * 5. Reactive refresh on 402 responses via SUBSCRIPTION_EXPIRED_EVENT
 *
 * Staleness rules:
 * - lastSuccessfulFetch tracks when we last got a valid response
 * - Periodic/visibility refreshes only fire if data is older than REFRESH_INTERVAL_MS
 * - On error, stale data is preserved (no flash to "free tier" on network blips)
 * - Forced refreshes (checkout, 402) always fetch regardless of staleness
 *
 * The backend's GET /api/subscription/status endpoint triggers its own
 * Stripe sync (also throttled), so each frontend refresh drives the full
 * read-through chain: React state → backend cache → Stripe API.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useInterval, useDocumentVisibility } from '@mantine/hooks';
import { useAuth } from './AuthContext';
import { useConsent } from './ConsentContext';
import { getSubscriptionStatus } from '../services/subscriptionService';
import { SUBSCRIPTION_EXPIRED_EVENT } from '../services/apiClient';
import {
  hashEmailForEnhancedConversions,
  hasRecordedSubscriptionPurchaseThisSession,
  syncConsentBeforeConversion,
  trackFreeSignup,
  trackOnboardingCompleted,
} from '../services/marketingTracking';
import { flushCookieConsentToServer } from '../services/flushCookieConsent';
import {
  clearSignupUtmSnapshot,
  resolveSignupUtmPayload,
} from '../services/acquisitionAttributionService';
import type { ConsentState } from '../types/consent';
import type { SubscriptionStatus } from '../types/subscription';
import { SubscriptionFeatures } from '../types/subscription';
import { SubscriptionContext, type SubscriptionContextType } from './SubscriptionContext';

/** How often to automatically refresh subscription status (milliseconds) */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Pushes `free_signup` when marketing is configured and consent allows hashing.
 * Fire-and-forget from fetchStatus so subscription refresh is not blocked.
 */
async function fireFreeSignupConversion(
  userId: string,
  userEmail: string | undefined,
  marketingConsent: boolean,
  consentState: ConsentState,
  isConsentSet: boolean,
): Promise<void> {
  syncConsentBeforeConversion(consentState, isConsentSet);

  // Persist consent ledger before Ads Free signup so admin can reconcile gclid rows (#1154).
  await flushCookieConsentToServer();

  let hashedEmail: string | undefined;

  if (marketingConsent && userEmail) {
    hashedEmail = await hashEmailForEnhancedConversions(userEmail);
  }

  const utmPayload = resolveSignupUtmPayload();
  trackFreeSignup({
    userId,
    hashedEmail,
    utmSource: utmPayload.utmSource,
    utmMedium: utmPayload.utmMedium,
    utmCampaign: utmPayload.utmCampaign,
  });
  clearSignupUtmSnapshot();
  trackOnboardingCompleted(userId);
}

interface SubscriptionProviderProps {
  children: ReactNode;
}

/**
 * Provides subscription state to the component tree.
 * Must be placed inside AuthProvider since it depends on authentication state.
 *
 * Context object and `useSubscription` live in `SubscriptionContext.ts` so this
 * file stays a Fast Refresh component-only boundary.
 */
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { isAuthenticated, termsAccepted, user } = useAuth();
  const { consent, isConsentSet } = useConsent();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const documentVisibility = useDocumentVisibility();

  /** Timestamp of the last successful API response */
  const lastSuccessfulFetchRef = useRef<number>(0);

  /**
   * Previous `requiresOnboarding` from the last successful status fetch.
   * Used to detect the onboarding-complete transition for free_signup.
   */
  const prevRequiresOnboardingRef = useRef<boolean | undefined>(undefined);

  /**
   * True once this session has seen an active subscription or a paid conversion.
   * Guards against mislabeling a paying user as free_signup when status fields
   * refresh in a non-atomic order.
   */
  const subscribedThisSessionRef = useRef(false);

  /** Latest auth/consent for fetchStatus without widening its dependency array. */
  const userRef = useRef(user);
  userRef.current = user;
  const consentRef = useRef(consent);
  consentRef.current = consent;
  const isConsentSetRef = useRef(isConsentSet);
  isConsentSetRef.current = isConsentSet;

  /*
   * Tracks the most recently scheduled fetch's promise. New callers chain
   * a NEW fetch onto this promise instead of short-circuiting, which
   * preserves the contract every caller relies on:
   *
   *   "After `await refreshSubscription()` resolves, the context reflects
   *    state from a fetch that started AT OR AFTER my call."
   *
   * The previous boolean `isFetchingRef.current = true; return;` guard
   * silently broke that contract — concurrent callers got `Promise<void>`
   * resolved instantly with stale state. That's the root cause of the
   * post-signup `WelcomeOnboarding`-overlay flake (see
   * ReviewDraftModal.scheduleCardForContact and the matching flower
   * variant): `await completeOnboarding(); await refreshSubscription();`
   * looked correct but the refresh was a no-op whenever the initial
   * auth/terms fetch was still in flight, leaving a stale
   * `requiresOnboarding=true` to flip the overlay on once the guest
   * draft was cleared. Every other site that pairs completeOnboarding
   * with refreshSubscription (UpgradeModal does it twice) had the same
   * latent bug.
   *
   * Note: the periodic and visibility refreshers go through
   * `refreshIfStale` first, which short-circuits on the staleness
   * check, so they don't multiply fetch traffic in steady state.
   */
  const inFlightFetchRef = useRef<Promise<void> | null>(null);

  /**
   * Core fetch function. On success, updates state and records the timestamp.
   * On error, preserves existing data to avoid flashing the user to free tier.
   *
   * Concurrency: each invocation chains a fresh fetch onto whatever fetch
   * is currently in flight (or runs immediately if none is). The awaited
   * promise resolves only after this caller's fetch (not an earlier one)
   * has updated state — see `inFlightFetchRef` doc above for why.
   *
   * @param forceRefresh When true, tells the backend to bypass its cache and
   *   sync throttle so data is read live from Stripe. Used on the account page.
   */
  const fetchStatus = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated || !termsAccepted) {
      setSubscription(null);
      prevRequiresOnboardingRef.current = undefined;
      subscribedThisSessionRef.current = false;
      return;
    }

    // Capture whatever's in flight at the moment we're called. Each new
    // caller awaits the previous chain, then runs its own fetch. Multiple
    // concurrent callers serialize through this ref so we never have two
    // overlapping API requests, but we DO guarantee a fresh fetch per call.
    const previous = inFlightFetchRef.current;

    const next: Promise<void> = (async () => {
      if (previous) {
        // Swallow the previous fetch's error here — it was already logged
        // by its own catch block; we're starting a fresh attempt.
        try {
          await previous;
        } catch {
          /* previous fetch's concern */
        }
      }

      setIsLoading((prev) => (subscription === null ? true : prev));
      try {
        const status = await getSubscriptionStatus(forceRefresh);

        const prevRequiresOnboarding = prevRequiresOnboardingRef.current;
        const completedFreeOnboarding =
          prevRequiresOnboarding === true
          && status.requiresOnboarding === false
          && !status.isSubscribed
          && !hasRecordedSubscriptionPurchaseThisSession()
          && !subscribedThisSessionRef.current;

        if (completedFreeOnboarding && userRef.current?.id) {
          void fireFreeSignupConversion(
            userRef.current.id,
            userRef.current.email,
            consentRef.current.marketing,
            consentRef.current,
            isConsentSetRef.current,
          );
        }

        if (status.isSubscribed || hasRecordedSubscriptionPurchaseThisSession()) {
          subscribedThisSessionRef.current = true;
        }

        prevRequiresOnboardingRef.current = status.requiresOnboarding;

        setSubscription(status);
        lastSuccessfulFetchRef.current = Date.now();
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        // Preserve stale data on error -- better to show potentially outdated
        // subscription info than to flash the user to "free tier" on a network blip.
        // Only clear if we never had data (initial load failure).
        if (lastSuccessfulFetchRef.current === 0) {
          setSubscription(null);
        }
      } finally {
        setIsLoading(false);
      }
    })();

    inFlightFetchRef.current = next;
    try {
      await next;
    } finally {
      // Only clear the ref if THIS call is still the tail of the chain; a
      // later caller may have already replaced it with their own promise.
      if (inFlightFetchRef.current === next) {
        inFlightFetchRef.current = null;
      }
    }
  }, [isAuthenticated, termsAccepted, subscription]);

  /** Returns true if the cached data is older than REFRESH_INTERVAL_MS */
  const isStale = useCallback(() => {
    return Date.now() - lastSuccessfulFetchRef.current > REFRESH_INTERVAL_MS;
  }, []);

  /**
   * Force-refresh from Stripe, bypassing all backend caches and sync throttle.
   * Used on the account page so the subscription section always reflects
   * the live Stripe state (e.g. after cancelling via customer service).
   */
  const forceRefreshFromStripe = useCallback(async () => {
    lastSuccessfulFetchRef.current = 0;
    await fetchStatus(true);
  }, [fetchStatus]);

  /**
   * Refresh only if data is stale. Used by periodic and visibility triggers
   * to avoid redundant API calls when data was recently fetched.
   */
  const refreshIfStale = useCallback(() => {
    if (isStale()) {
      fetchStatus();
    }
  }, [isStale, fetchStatus]);

  // Initial fetch when auth state changes
  useEffect(() => {
    lastSuccessfulFetchRef.current = 0;
    fetchStatus();
  }, [isAuthenticated, termsAccepted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic refresh using Mantine's useInterval.
  // Only actually fetches if the data is stale (timestamp check).
  const interval = useInterval(refreshIfStale, REFRESH_INTERVAL_MS);

  useEffect(() => {
    if (isAuthenticated && termsAccepted) {
      interval.start();
    } else {
      interval.stop();
    }
    return () => interval.stop();
  }, [isAuthenticated, termsAccepted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tab-focus refresh: when the user switches back to this tab, check if
  // data is stale and refresh. Catches changes that happened while idle
  // (e.g., user canceled in Stripe Customer Portal in another tab).
  useEffect(() => {
    if (documentVisibility === 'visible' && isAuthenticated && termsAccepted) {
      refreshIfStale();
    }
  }, [documentVisibility]); // eslint-disable-line react-hooks/exhaustive-deps

  // 402 handler: when any API call returns 402 (Payment Required), the
  // apiClient dispatches this event. Force-refresh immediately since the
  // backend just told us the subscription is invalid.
  useEffect(() => {
    const handleSubscriptionExpired = () => {
      // Force refresh -- bypass staleness check since backend explicitly said 402
      lastSuccessfulFetchRef.current = 0;
      fetchStatus();
    };

    window.addEventListener(SUBSCRIPTION_EXPIRED_EVENT, handleSubscriptionExpired);
    return () => window.removeEventListener(SUBSCRIPTION_EXPIRED_EVENT, handleSubscriptionExpired);
  }, [fetchStatus]);

  const isSubscribed = subscription?.isSubscribed ?? false;
  const canAutoSend = subscription?.features.includes(SubscriptionFeatures.AutoSend) ?? false;
  const canAddContact = subscription
    ? subscription.contactLimit === null || subscription.activeContactCount < subscription.contactLimit
    : true;
  const contactLimit = subscription?.contactLimit ?? null;
  const requiresContactSelection = subscription?.requiresContactSelection ?? false;
  const requiresOnboarding = subscription?.requiresOnboarding ?? false;
  const requiresWelcomePlanWall = subscription?.requiresWelcomePlanWall ?? false;

  const value: SubscriptionContextType = {
    subscription,
    isLoading,
    isSubscribed,
    canAutoSend,
    canAddContact,
    contactLimit,
    requiresContactSelection,
    requiresOnboarding,
    requiresWelcomePlanWall,
    refreshSubscription: fetchStatus,
    forceRefreshFromStripe,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
